import { lazy, Suspense, useEffect, useRef } from "react";
import "@/App.css";
import Lenis from "lenis";
import { Toaster } from "sonner";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Hero from "@/components/Hero";
import Marquee from "@/components/Marquee";
import Features from "@/components/Features";
import Compare from "@/components/Compare";
import Manifesto from "@/components/Manifesto";
import Calculator from "@/components/Calculator";
import Footer from "@/components/Footer";
import InstallPrompt from "@/components/InstallPrompt";
import FleetDeal from "@/components/FleetDeal";
import FavesSection from "@/components/FavesSection";
import { api, setToken } from "@/lib/api";
import { applyLang, getLang } from "@/lib/i18n";
import LanguageOnboarding from "@/components/LanguageOnboarding";
import RequirePaid from "@/components/RequirePaid";

// Heavy chunks load on demand — keeps the first download (and PWA install) fast
const LiveMap = lazy(() => import("@/components/LiveMap"));
const Login = lazy(() => import("@/pages/Login"));
const Fleet = lazy(() => import("@/pages/Fleet"));
const Paywall = lazy(() => import("@/pages/Paywall"));
const PaymentResult = lazy(() => import("@/pages/PaymentResult"));
const Admin = lazy(() => import("@/pages/Admin"));
const FleetAccess = lazy(() => import("@/pages/FleetAccess"));
const DriverHome = lazy(() => import("@/pages/DriverHome"));
const MiniCab = lazy(() => import("@/pages/MiniCab"));
const FeaturesPage = lazy(() => import("@/pages/Features"));

const PageLoader = (
    <div className="flex min-h-screen items-center justify-center bg-[#161413]">
        <p className="font-display animate-pulse text-3xl tracking-widest text-[#E5A93C]">LOADING…</p>
    </div>
);

function Landing() {
    return (
        <>
            <Navbar />
            <main>
                <Hero />
                <Marquee />
                <FleetDeal />
                <Suspense fallback={<div className="min-h-[600px] bg-[#161413]" />}>
                    <LiveMap />
                </Suspense>
                <Features />
                <Compare />
                <Manifesto />
                <Calculator />
                <FavesSection />
            </main>
            <Footer />
        </>
    );
}

function AuthCallback() {
    const location = useLocation();
    const navigate = useNavigate();
    const hasProcessed = useRef(false);

    useEffect(() => {
        if (hasProcessed.current) return;
        hasProcessed.current = true;
        const match = location.hash.match(/session_id=([^&]+)/);
        if (!match) {
            navigate("/login");
            return;
        }
        api("/auth/google-session", { method: "POST", body: { session_id: match[1] } })
            .then((data) => {
                setToken(data.session_token);
                window.history.replaceState(null, "", window.location.pathname);
                navigate("/paywall");
            })
            .catch(() => navigate("/login"));
    }, [location, navigate]);

    return (
        <div className="flex min-h-screen items-center justify-center bg-[#161413]">
            <p className="font-display animate-pulse text-3xl tracking-widest text-[#E5A93C]">
                CHECKING CREDENTIALS…
            </p>
        </div>
    );
}

function AppRouter() {
    const location = useLocation();
    if (location.hash?.includes("session_id=")) {
        return <AuthCallback />;
    }
    return (
        <Suspense fallback={PageLoader}>
            <Routes>
                <Route path="/" element={<Landing />} />
                <Route path="/login" element={<Login />} />
                <Route path="/fleet" element={<Fleet />} />
                <Route path="/paywall" element={<Paywall />} />
                <Route path="/access" element={<FleetAccess />} />
                <Route
                    path="/driver"
                    element={
                        <RequirePaid>
                            <DriverHome />
                        </RequirePaid>
                    }
                />
                <Route
                    path="/mini"
                    element={
                        <RequirePaid>
                            <MiniCab />
                        </RequirePaid>
                    }
                />
                <Route path="/features" element={<FeaturesPage />} />
                <Route path="/write-route" element={<Navigate to="/" replace />} />
                <Route path="/payment/success" element={<PaymentResult success />} />
                <Route path="/payment/cancel" element={<PaymentResult />} />
                <Route path="/admin" element={<Admin />} />
            </Routes>
            <LanguageOnboarding />
        </Suspense>
    );
}

function App() {
    useEffect(() => {
        // Returning driver's chosen language — applies to every screen as it
        // renders (the i18n observer catches late mounts like the lazy map)
        if (getLang() !== "en") applyLang(getLang());
        const lenis = new Lenis({
            smoothWheel: true,
            duration: 1.2,
            easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
        });
        window.__lenis = lenis;
        let rafId;
        const raf = (time) => {
            lenis.raf(time);
            rafId = requestAnimationFrame(raf);
        };
        rafId = requestAnimationFrame(raf);
        return () => {
            cancelAnimationFrame(rafId);
            lenis.destroy();
            window.__lenis = null;
        };
    }, []);

    return (
        <BrowserRouter>
            <div className="font-mono2 min-h-screen overflow-x-hidden bg-[#161413] text-[#F6F2E8]">
                <div className="grain-overlay" aria-hidden="true" />
                <AppRouter />
                <InstallPrompt />
                <Toaster
                    position="bottom-right"
                    toastOptions={{
                        style: {
                            background: "#161413",
                            border: "2px solid #E5A93C",
                            color: "#F6F2E8",
                            fontFamily: "'Space Mono', monospace",
                            borderRadius: 0,
                        },
                    }}
                />
            </div>
        </BrowserRouter>
    );
}

export default App;
